<template>
  <div>
    <!-- File Upload Section -->
    <div class="d-flex align-center mb-4">
      <!-- Camera Button for Taking Photos -->
      <v-btn
        icon="mdi-camera"
        color="primary"
        variant="outlined"
        :disabled="uploading"
        @click="triggerCameraInput"
        size="large"
        class="me-2"
        v-if="mobile"
      >
      </v-btn>
      <!-- Hidden Camera Input -->
      <input
        ref="cameraInputRef"
        type="file"
        :accept="props.category === 'photo' ? 'image/*' : acceptedFileTypes"
        :multiple="true"
        capture="environment"
        style="display: none"
        @change="onCameraFileSelect"
      />

      <!-- File Upload Button -->
      <v-btn
        icon="mdi-file-upload"
        v-if="mobile"
        color="primary"
        variant="outlined"
        :disabled="uploading"
        @click="triggerFileInput"
        size="large"
        class="me-2"
      >
      </v-btn>
      <!-- Hidden File Input (without capture) -->
      <input
        ref="fileInputRef"
        type="file"
        :accept="acceptedFileTypes"
        :multiple="true"
        style="display: none"
        @change="onFileSelect"
      />
      <v-file-upload
        v-if="!mobile"
        density="compact"
        v-model="selectedFiles"
        :accept="acceptedFileTypes"
        height="56"
        :multiple="true"
        :disabled="uploading"
        @change="onFileSelectVuetify"
        class="flex-grow-1"
      />
    </div>

    <!-- Upload Progress -->
    <v-progress-linear
      v-if="uploading"
      :model-value="uploadProgress"
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <!-- Upload Error -->
    <v-alert
      v-if="uploadError"
      type="error"
      variant="tonal"
      class="mb-4"
      closable
      @click:close="uploadError = null"
    >
      {{ uploadError }}
    </v-alert>

    <!-- Loading State for Files -->
    <div v-if="loadingFiles" class="mb-4">
      <v-skeleton-loader type="card" class="mb-2"></v-skeleton-loader>
      <v-skeleton-loader type="card" class="mb-2"></v-skeleton-loader>
    </div>

    <!-- Existing Files Section -->
    <div v-else-if="filteredExistingFiles.length > 0" class="mb-4">
      <v-card-title class="pa-0 mb-3">
        <v-icon start>mdi-file-document-multiple</v-icon>
        {{ t(strings.existingFiles) }}
      </v-card-title>
      <v-row>
        <v-col v-for="file in filteredExistingFiles" :key="file.id" cols="4">
          <v-card variant="outlined" class="h-100">
            <v-card-text class="pa-3 position-relative">
              <!-- Delete Button -->
              <v-btn
                icon="mdi-close"
                size="x-small"
                color="error"
                variant="text"
                class="position-absolute"
                style="top: 8px; right: 8px; z-index: 1"
                @click="onDeleteFile(file)"
                :disabled="deletingFiles.has(file.id)"
                :loading="deletingFiles.has(file.id)"
              >
              </v-btn>

              <!-- File Icon -->
              <div class="d-flex justify-center mb-3">
                <v-img
                  v-if="isImage(file.mimetype) && file.url"
                  :src="file.url"
                  :alt="file.file_original_name"
                  width="48"
                  height="48"
                />
                <v-icon v-else size="48" color="primary">
                  {{ getFileIcon(file.mimetype) }}
                </v-icon>
              </div>

              <!-- File Name -->
              <div class="text-center mb-3">
                <div
                  class="text-body-2 font-weight-medium text-truncate"
                  :title="file.file_original_name"
                >
                  <a :href="file.url" target="_blank" v-if="file.url"
                    ><v-icon size="16" color="primary"
                      >mdi-open-in-new</v-icon
                    ></a
                  >
                  {{ file.file_original_name }}
                </div>
              </div>

              <!-- File Metadata -->
              <div class="text-caption text-medium-emphasis">
                <div class="d-flex justify-space-between">
                  <span>{{ formatFileSize(file.size) }}</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span>{{ formatDate(file.created_at) }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- No Files Message -->
    <v-alert v-else-if="!uploading" type="info" variant="tonal" class="mb-4">
      {{ t(strings.noFilesForCategory) }}
    </v-alert>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon start color="error">mdi-delete</v-icon>
          Delete File
        </v-card-title>

        <v-card-text>
          <p>Are you sure you want to delete this file?</p>
          <v-alert type="warning" variant="tonal" class="mt-3">
            This action cannot be undone.
          </v-alert>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            @click="showDeleteDialog = false"
            :disabled="deletingFiles.has(fileToDelete?.id || '')"
          >
            {{ t(strings.cancel) }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            @click="confirmDelete"
            :loading="deletingFiles.has(fileToDelete?.id || '')"
            :disabled="!fileToDelete"
          >
            {{ t(strings.delete) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { getTanstackErrorMessage, TanstackError } from "@saflib/sdk";
import { part_file_uploader_strings as strings } from "./PartFileUploader.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { formatDate } from "../../lib/index.ts";
import {
  useUploadPartFile,
  useDeletePartFile,
} from "../../requests/parts/index.ts";
import type { PartFile } from "@vendata/power-up-spec";
import { ref, computed, watch } from "vue";
import { VFileUpload } from "vuetify/components";
const selectedFiles = ref<File[]>([]);

import { useDisplay } from "vuetify";

const { mobile } = useDisplay();
type HTMLInputElementRef = HTMLInputElement | null;

interface Props {
  partId: string;
  category:
    | "pedigree"
    | "shop_release"
    | "flow_test_report"
    | "dimensional_inspection"
    | "nde_report"
    | "photo";
  existingFiles: PartFile[];
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const { t } = useReverseT();

// File upload state
const uploading = ref(false);
const uploadProgress = ref(0);
const uploadError = ref<string | null>(null);
const deletingFiles = ref(new Set<string>());
const cameraInputRef = ref<HTMLInputElementRef>(null);
const fileInputRef = ref<HTMLInputElementRef>(null);

// Delete confirmation dialog state
const showDeleteDialog = ref(false);
const fileToDelete = ref<PartFile | null>(null);

// File upload configuration
const allowedMimes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];
const acceptedFileTypes =
  props.category === "photo" ? "image/*" : allowedMimes.join(",");

// Mutations
const uploadMutation = useUploadPartFile();
const deleteMutation = useDeletePartFile();

// Computed properties
const loadingFiles = computed(() => props.loading);
const filteredExistingFiles = computed(() => {
  return props.existingFiles.filter((file) => file.category === props.category);
});

// Trigger camera input
const triggerCameraInput = () => {
  cameraInputRef.value?.click();
};

// Trigger file input
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// Camera file selection handler
const onCameraFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) return;

  const fileArray = Array.from(target.files);
  await uploadFiles(fileArray);

  // Reset camera input
  if (cameraInputRef.value) {
    cameraInputRef.value.value = "";
  }
};

const onFileSelectVuetify = async () => {
  const fileArray = Array.from(selectedFiles.value);
  if (fileArray.length === 0) return;

  await uploadFiles(fileArray);

  // Reset form
  selectedFiles.value = [];
};

// File selection handler for file upload button
const onFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) return;

  const fileArray = Array.from(target.files);
  await uploadFiles(fileArray);

  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

// Shared upload logic
const uploadFiles = async (fileArray: File[]) => {
  uploading.value = true;
  uploadProgress.value = 0;
  uploadError.value = null;

  try {
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];

      // Update progress
      uploadProgress.value = ((i + 1) / fileArray.length) * 100;

      // Upload file
      const result = await uploadMutation.mutateAsync({
        partId: props.partId,
        file: file,
        category: props.category,
      });

      // Emit event for parent to handle file addition
      emit("file-uploaded", result);
    }

    uploadProgress.value = 100;
  } catch (error) {
    console.error("Upload failed:", error);
    if (error instanceof TanstackError) {
      uploadError.value = getTanstackErrorMessage(error);
      if (error.code === "INVALID_FILE_TYPE") {
        uploadError.value = t(strings.unsupportedFileType);
      }
    } else {
      uploadError.value =
        error instanceof Error ? error.message : "Upload failed";
    }
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
  }
};

// Delete file handler - show confirmation dialog
const onDeleteFile = (file: PartFile) => {
  fileToDelete.value = file;
  showDeleteDialog.value = true;
};

// Confirm delete handler
const confirmDelete = async () => {
  if (!fileToDelete.value) return;

  const file = fileToDelete.value;
  if (deletingFiles.value.has(file.id)) return;

  deletingFiles.value.add(file.id);

  try {
    await deleteMutation.mutateAsync({
      partId: file.part_id,
      fileId: file.id,
    });

    // Emit event for parent to handle file removal
    emit("file-deleted", file);

    // Close dialog
    showDeleteDialog.value = false;
    fileToDelete.value = null;
  } catch (error) {
    uploadError.value =
      error instanceof Error ? error.message : "Delete failed";
  } finally {
    deletingFiles.value.delete(file.id);
  }
};

// Utility functions
const isImage = (mimetype: string) => {
  return mimetype.includes("image");
};

const getFileIcon = (mimetype: string) => {
  if (mimetype.includes("pdf")) return "mdi-file-pdf-box";
  if (mimetype.includes("word") || mimetype.includes("document"))
    return "mdi-file-word-box";
  if (mimetype.includes("image")) return "mdi-file-image";
  if (mimetype.includes("text")) return "mdi-file-document-outline";
  return "mdi-file-outline";
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Emits
const emit = defineEmits<{
  "file-uploaded": [file: PartFile];
  "file-deleted": [file: PartFile];
}>();

// Watch for category changes to clear errors
watch(
  () => props.category,
  () => {
    uploadError.value = null;
  },
);
</script>
